import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { LOGOUT } from './action/AuthAction';
import AuthService from 'service/AuthService';
import './Home.scss';
import LoadingSpinner from 'component/LoadingSpinner';
import ProtectedLink from './helpers/ProtectedLink';
import {
  hasDashboardAccess,
  hasMonitorAccess,
  hasProjectAccess,
  hasClientAccess,
  hasPrincipalAccess,
  hasStoreAccess,
  hasAreaAccess,
  hasGoodsAccess,
  hasInstallerAccess,
  hasUserAccess,
  hasRetailPartnerAccess,
  hasFormAccess,
  hasRBACAccess,

  hasVersionAccess,
  hasPrivilege,
  hasTransactionAccess,
  hasSystemParameterAccess,
  hasProjectConfigAccess,
  hasProjectKeyAccess,
  hasProjectLayoutAccess,
  hasProjectFilterAccess,
  hasProjectMetricAccess,
  hasProjectUserAccess,
  hasHomeShortcutAccess,
  hasPpobAccess,
  hasTribeAccess,
  hasPoiAccess,
  hasStoreDirectorySupplyMonitorAccess,
  hasQCMonitorAccess,
  hasUserGrowthMonitorAccess,
  hasCoverageAnalysisAccess,

  hasDistributionHubAccess,
  hasNexusDistributorAccess
} from './helpers/hasPrivilege';
import { NotificationProvider } from './contexts/NotificationContext';
import NotificationDropdown from './component/NotificationDropdown';
import { deleteFcmToken } from './notifications/firebase';

const Home: React.FC = () => {
  const authState = useSelector((state: any) => state.auth);
  const profile = authState?.profile;
  const token =
    authState?.profile?.data?.token ||
    authState?.profile?.token ||
    authState?.token;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileDropdownRef = useRef<HTMLDivElement>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.innerWidth >= 991.98;
  });
  const [isPpobSubmenuOpen, setIsPpobSubmenuOpen] = useState(() => location.pathname.includes("/ppob"));
  const hasDashboardPrivilege = hasDashboardAccess(profile);
  const hasMonitorPrivilege = hasMonitorAccess(profile);
  const hasQCMonitorPrivilege = hasQCMonitorAccess(profile);
  const hasUserGrowthMonitorPrivilege = hasUserGrowthMonitorAccess(profile);
  const hasCoverageAnalysisPrivilege = hasCoverageAnalysisAccess(profile);
  const hasProjectPrivilege = hasProjectAccess(profile);
  const hasClientPrivilege = hasClientAccess(profile);
  const hasPrincipalPrivilege = hasPrincipalAccess(profile);
  const hasStorePrivilege = hasStoreAccess(profile);
  const hasAreaPrivilege = hasAreaAccess(profile);
  const hasGoodsPrivilege = hasGoodsAccess(profile);
  const hasInstallerPrivilege = hasInstallerAccess(profile);
  const hasUserPrivilege = hasUserAccess(profile);
  const hasRetailPartnerPrivilege = hasRetailPartnerAccess(profile);
  const hasFormPrivilege = hasFormAccess(profile);
  const hasRBACPrivilege = hasRBACAccess(profile);
  const hasVersionPrivilege = hasVersionAccess(profile);

  const hasBankAccountPrivilege = hasPrivilege(profile, 'amar_bank_account_registration', 'view');
  const hasQrisTransactionPrivilege = hasPrivilege(profile, 'amar_qris_transaction_status', 'view');
  const hasTimeDepositPrivilege = hasPrivilege(profile, 'amar_time_deposit_status', 'view');
  const hasTransferStatusPrivilege = hasPrivilege(profile, 'amar_transfer_status', 'view');
  const hasDisbursementPrivilege = hasPrivilege(profile, 'disbursement', 'view');
  const hasReportEnginePrivilege = hasPrivilege(profile, 'report_engine', 'view');
  const hasTransactionPrivilege = hasTransactionAccess(profile);
  const hasSystemParameterPrivilege = hasSystemParameterAccess(profile)
  const hasProjectConfigPrivilege = hasProjectConfigAccess(profile)
  const hasProjectKeyPrivilege = hasProjectKeyAccess(profile)
  const hasProjectLayoutPrivilege = hasProjectLayoutAccess(profile)
  const hasProjectFilterPrivilege = hasProjectFilterAccess(profile)
  const hasProjectMetricPrivilege = hasProjectMetricAccess(profile)
  const hasProjectUserPrivilege = hasProjectUserAccess(profile)
  const hasHomeShortcutPrivilege = hasHomeShortcutAccess(profile)
  const hasPpobPrivilege = hasPpobAccess(profile)
  const hasTribePrivilege = hasTribeAccess(profile);
  const hasPoiPrivilege = hasPoiAccess(profile);
  const hasStoreDirectorySupplyMonitorPrivilege = hasStoreDirectorySupplyMonitorAccess(profile);
  const hasNexusDistributorPrivilege = hasNexusDistributorAccess(profile);

  const hasDistributionHubPrivilege = hasDistributionHubAccess(profile);

  const hasClientTransmoraSection = hasProjectConfigPrivilege || hasProjectKeyPrivilege || hasProjectLayoutPrivilege ||
    hasProjectFilterPrivilege || hasProjectMetricPrivilege || hasProjectUserPrivilege || hasStoreDirectorySupplyMonitorPrivilege;

  const hasNexusDistributorSection = hasNexusDistributorPrivilege;

  const hasDataSection = hasClientPrivilege || hasPrincipalPrivilege || hasStorePrivilege || hasAreaPrivilege ||
    hasGoodsPrivilege || hasInstallerPrivilege || hasUserPrivilege ||
    hasRetailPartnerPrivilege || hasFormPrivilege || hasTransactionPrivilege || hasTribePrivilege || hasPoiPrivilege || hasDistributionHubPrivilege;


  const hasBankIntegrationSection = hasBankAccountPrivilege || hasQrisTransactionPrivilege ||
    hasTimeDepositPrivilege || hasTransferStatusPrivilege;

  const hasConfigurationSection = hasRBACPrivilege || hasVersionPrivilege ||
    hasHomeShortcutPrivilege || hasPpobPrivilege;


  const hasReportSection = hasDisbursementPrivilege || hasReportEnginePrivilege;

  useEffect(() => {
    const currentPath = location.pathname;
    if (!checkRouteAccess(currentPath)) {
      navigate('/dashboard');
    }
  }, [location.pathname, profile, navigate]);

  // Close profile dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(e.target as Node)) {
        setIsProfileMenuOpen(false);
      }
    };
    if (isProfileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileMenuOpen]);

  // Close profile dropdown on route change
  useEffect(() => {
    setIsProfileMenuOpen(false);
  }, [location.pathname]);



  const checkRouteAccess = (path: string): boolean => {
    const routeAccessMap: { [key: string]: boolean } = {
      '/dashboard': hasDashboardPrivilege,
      '/monitor': hasMonitorPrivilege,
      '/monitor/overview': hasMonitorPrivilege,
      '/qc-monitor': hasQCMonitorPrivilege,
      '/growth-monitor': hasUserGrowthMonitorPrivilege,
      '/coverage-analysis': hasCoverageAnalysisPrivilege,
      '/project': hasProjectPrivilege,
      '/client-project-config': hasProjectConfigPrivilege,
      '/client-project-key': hasProjectKeyPrivilege,
      '/client-project-layout': hasProjectLayoutPrivilege,
      '/client-project-filter': hasProjectFilterPrivilege,
      '/client-project-metric': hasProjectMetricPrivilege,
      '/client-project-user': hasProjectUserPrivilege,

      '/client': hasClientPrivilege,
      '/principal': hasPrincipalPrivilege,
      '/store': hasStorePrivilege,
      '/area': hasAreaPrivilege,
      '/goods': hasGoodsPrivilege,
      '/installer': hasInstallerPrivilege,
      '/user': hasUserPrivilege,
      '/retail-partner': hasRetailPartnerPrivilege,

      '/form': hasFormPrivilege,
      '/transaction': hasTransactionPrivilege,
      '/rbac': hasRBACPrivilege,
      '/version': hasVersionPrivilege,

      '/disbursement': hasDisbursementPrivilege,
      '/report-engine': hasReportEnginePrivilege,
      '/bankAccount': hasBankAccountPrivilege,
      '/qristransaction': hasQrisTransactionPrivilege,
      '/timeDeposit': hasTimeDepositPrivilege,
      '/transferStatus': hasTransferStatusPrivilege,
      '/mobile-shortcut': hasHomeShortcutPrivilege,
      '/ppob': hasPpobPrivilege
    };

    if (routeAccessMap[path] !== undefined) {
      return routeAccessMap[path];
    }

    for (const [route, hasAccess] of Object.entries(routeAccessMap)) {
      if (path.startsWith(route)) {
        return hasAccess;
      }
    }

    return true;
  };

  const handleLogout = async () => {
    if (token) {
      try {
        await AuthService().updateFcmToken("", token);
        console.log("[Logout] Successfully reset FCM token on backend");
      } catch (err) {
        console.warn("[Logout] Failed to reset FCM token on backend:", err);
      }
    }
    try {
      await deleteFcmToken();
      console.log("[Logout] Successfully deleted local FCM token");
    } catch (err) {
      console.warn("[Logout] Failed to delete local FCM token:", err);
    }
    dispatch({ type: LOGOUT });
    navigate("/signin");
  };

  const handleSidebarMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    const target = event.target as HTMLElement | null;
    const linkEl = target?.closest('a') as HTMLElement | null;

    if (linkEl) {
      // Add ripple animation effect
      const rect = linkEl.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      const ripple = document.createElement('span');
      ripple.className = 'click-ripple';
      ripple.style.left = `${x}px`;
      ripple.style.top = `${y}px`;
      linkEl.appendChild(ripple);

      setTimeout(() => {
        ripple.remove();
      }, 550);

      // Close sidebar on mobile/tablet view
      if (isSidebarOpen && typeof window !== 'undefined') {
        const isMobileOrTablet = window.matchMedia('(max-width: 991.98px)').matches;
        if (isMobileOrTablet) {
          setIsSidebarOpen(false);
        }
      }
    }
  };

  return (
    <NotificationProvider>
      <div style={{ display: 'flex', minHeight: '100vh' }}>
        <div
          className={`sidebar-container ${isSidebarOpen ? 'is-open' : ''}`}
          style={{
            width: isSidebarOpen ? 250 : 0,
            flexShrink: 0,
            background: '#ffffff',
            display: 'flex',
            flexDirection: 'column',
            borderRight: isSidebarOpen ? '1px solid #ddd' : '0px solid transparent',
            height: '100vh',
            overflowY: 'auto',
            overflowX: 'hidden',
            transition: 'width 0.2s ease',
            position: 'relative',
            zIndex: 1040,
          }}
        >
          <nav
            className="p-0 sidebar"
            onClick={handleSidebarMenuClick}
            style={{
              flex: 'unset',
              width: 250,
              opacity: isSidebarOpen ? 1 : 0,
              transition: 'opacity 0.15s ease',
              pointerEvents: isSidebarOpen ? 'auto' : 'none',
            }}
          >
            <img
              src="/assets/logo/logo-transmore-hd.png"
              className="mx-auto rounded img-fluid d-block"
              alt=""
              style={{ height: '10vh' }}
            />
            <ul className="p-0">
              {/* <li>
              <ul className="p-0">
                <li className="tm-text-primary">
                  <Link to="/dashboard" className={`text-decoration-none ${location.pathname.includes("/dashboard") ? 'active' : ''}`}>
                    <div className="icon-wrapper"><i className="fa-solid fa-table"></i></div>
                    <div className="text-wrapper"><span>Dashboard</span></div>
                  </Link>
                </li>
                <li className="tm-text-primary">
                  <Link to="/monitor" className={`text-decoration-none ${location.pathname.includes("/monitor") ? 'active' : ''}`}>
                    <div className="icon-wrapper"><i className="fa-solid fa-desktop"></i></div>
                    <div className="text-wrapper"><span>Monitor</span></div>
                  </Link>
                </li>
                <li className="tm-text-primary">
                  <Link to="/project" className={`text-decoration-none ${location.pathname.includes("/project") ? 'active' : ''}`}>
                    <div className="icon-wrapper"><i className="fa-solid fa-diagram-project"></i></div>
                    <div className="text-wrapper"><span>Projects</span></div>
                  </Link>
                </li>
              </ul>
            </li>
            <li>
              <p>Datas</p>
              <ul className="p-0">
                <li className="tm-text-primary">
                  <Link to="/client" className={`text-decoration-none ${location.pathname.includes("/client") ? 'active' : ''}`}>
                    <div className="icon-wrapper"><i className="fa-solid fa-handshake"></i></div>
                    <div className="text-wrapper"><span>Client</span></div>
                  </Link>
                </li>
                <li className="tm-text-primary">
                  <Link to="/store" className={`text-decoration-none ${location.pathname.includes("/store") ? 'active' : ''}`}>
                    <div className="icon-wrapper"><i className="fa-solid fa-store"></i></div>
                    <div className="text-wrapper"><span>Store</span></div>
                  </Link>
                </li>
                <li className="tm-text-primary">
                  <Link to="/area" className={`text-decoration-none ${location.pathname.includes("/area") ? 'active' : ''}`}>
                    <div className="icon-wrapper"><i className="fa-solid fa-mountain-city"></i></div>
                    <div className="text-wrapper"><span>Area</span></div>
                  </Link>
                </li>
                <li className="tm-text-primary">
                  <Link to="/goods" className={`text-decoration-none ${location.pathname.includes("/goods") ? 'active' : ''}`}>
                    <div className="icon-wrapper"><i className="fa-solid fa-box"></i></div>
                    <div className="text-wrapper"><span>Goods</span></div>
                  </Link>
                </li>
                <li className="tm-text-primary">
                  <Link to="/installer" className={`text-decoration-none ${location.pathname.includes("/installer") ? 'active' : ''}`}>
                    <div className="icon-wrapper"><i className="fa-solid fa-users"></i></div>
                    <div className="text-wrapper"><span>Installer</span></div>
                  </Link>
                </li>
                <li className="tm-text-primary">
                  <Link to="/user" className={`text-decoration-none ${location.pathname.includes("/user") ? 'active' : ''}`}>
                    <div className="icon-wrapper"><i className="fa-solid fa-user"></i></div>
                    <div className="text-wrapper"><span>User</span></div>
                  </Link>
                </li>
                <li className="tm-text-primary">
                  <Link to="/retail-partner" className={`text-decoration-none ${location.pathname.includes("/retail-partner") ? 'active' : ''}`}>
                    <div className="icon-wrapper"><i className="fa-solid fa-handshake"></i></div>
                    <div className="text-wrapper"><span>Merchant Partner</span></div>
                  </Link>
                </li>
                <li className="tm-text-primary">
                  <Link to="/form" className={`text-decoration-none ${location.pathname.includes("/form") ? 'active' : ''}`}>
                    <div className="icon-wrapper"><i className="fa-solid fa-clipboard-list"></i></div>
                    <div className="text-wrapper"><span>Forms</span></div>
                  </Link>
                </li>
              </ul>
            </li>
            <li>
              <p>Amar Bank Integration</p>
              <ul className="p-0">
                <li className="tm-text-primary">
                  <Link to="/bankAccount" className={`text-decoration-none d-flex align-items-center ${location.pathname.includes("/bankAccount") ? 'active' : ''}`}>
                    <div className="icon-wrapper"><i className="fa-solid fa-building-columns"></i></div>
                    <div className="text-wrapper"><span>Bank Account Registration</span></div>
                  </Link>
                </li>
                <li className="tm-text-primary">
                  <Link to="/qristransaction" className={`text-decoration-none d-flex align-items-center ${location.pathname.includes("/qristransaction") ? 'active' : ''}`}>
                    <div className="icon-wrapper"><i className="fa-solid fa-qrcode"></i></div>
                    <div className="text-wrapper"><span>Qris Transaction Status</span></div>
                  </Link>
                </li>
                <li className="tm-text-primary">
                  <Link to="/timeDeposit" className={`text-decoration-none d-flex align-items-center ${location.pathname.includes("/timeDeposit") ? 'active' : ''}`}>
                    <div className="icon-wrapper"><i className="fa-solid fa-money-bill-transfer"></i></div>
                    <div className="text-wrapper"><span>Time Deposit Status</span></div>
                  </Link>
                </li>
                <li className="tm-text-primary">
                  <Link to="/transferStatus" className={`text-decoration-none ${location.pathname.includes("/transferStatus") ? 'active' : ''}`}>
                    <div className="icon-wrapper"><i className="fa-solid fa-money-check"></i></div>
                    <div className="text-wrapper"><span>Transfer Status</span></div>
                  </Link>
                </li>
              </ul>
            </li>
            <li>
              <p>Configurations</p>
              <ul className="p-0">
                <li className="tm-text-primary">
                  <Link to="/rbac" className={`text-decoration-none ${location.pathname.includes("/rbac") ? 'active' : ''}`}>
                    <div className="icon-wrapper"><i className="fa-solid fa-person"></i></div>
                    <div className="text-wrapper"><span>RBAC</span></div>
                  </Link>
                </li>
                <li className="tm-text-primary">
                  <Link to="/tag" className={`text-decoration-none ${location.pathname.includes("/tag") ? 'active' : ''}`}>
                    <div className="icon-wrapper"><i className="fa-solid fa-tag"></i></div>
                    <div className="text-wrapper"><span>Tags</span></div>
                  </Link>
                </li>
                <li className="tm-text-primary">
                  <Link to="/option" className={`text-decoration-none ${location.pathname.includes("/option") ? 'active' : ''}`}>
                    <div className="icon-wrapper"><i className="fa-solid fa-circle-notch"></i></div>
                    <div className="text-wrapper"><span>Options</span></div>
                  </Link>
                </li>
                <li className="tm-text-primary">
                  <Link to="/version" className={`text-decoration-none ${location.pathname.includes("/version") ? 'active' : ''}`}>
                    <div className="icon-wrapper"><i className="fa-solid fa-mobile-screen"></i></div>
                    <div className="text-wrapper"><span>Versions</span></div>
                  </Link>
                </li>
              </ul>
            </li> */}
              <li>
                <ul className="p-0">
                  {hasDashboardPrivilege && (
                    <li className="tm-text-primary">
                      <ProtectedLink
                        to="/dashboard"
                        vgroup="dashboard"
                        action="view"
                        applicationType="Admin Transmora"
                        className={`text-decoration-none ${location.pathname.includes("/dashboard") ? 'active' : ''}`}
                      >
                        <div className="icon-wrapper"><i className="fa-solid fa-table"></i></div>
                        <div className="text-wrapper"><span>Dashboard</span></div>
                      </ProtectedLink>
                    </li>
                  )}

                  {hasMonitorPrivilege && (
                    <li className="tm-text-primary">
                      <ProtectedLink
                        to="/monitor/overview"
                        vgroup="monitor"
                        action="view"
                        applicationType="Admin Transmora"
                        className={`text-decoration-none ${location.pathname.includes("/monitor") ? 'active' : ''}`}
                      >
                        <div className="icon-wrapper"><i className="fa-solid fa-desktop"></i></div>
                        <div className="text-wrapper"><span>Field Monitor</span></div>
                      </ProtectedLink>
                    </li>
                  )}

                  {hasQCMonitorPrivilege && (
                    <li className="tm-text-primary">
                      <ProtectedLink
                        to="/qc-monitor"
                        vgroup="monitor_qc"
                        action="view"
                        applicationType="Admin Transmora"
                        className={`text-decoration-none ${location.pathname.includes("/qc-monitor") ? 'active' : ''}`}
                      >
                        <div className="icon-wrapper"><i className="fa-solid fa-clipboard-check"></i></div>
                        <div className="text-wrapper"><span>QC Monitor</span></div>
                      </ProtectedLink>
                    </li>
                  )}

                  {hasUserGrowthMonitorPrivilege && (
                    <li className="tm-text-primary">
                      <ProtectedLink
                        to="/growth-monitor"
                        vgroup="monitor_growth"
                        action="view"
                        applicationType="Admin Transmora"
                        className={`text-decoration-none ${location.pathname.includes("/growth-monitor") ? 'active' : ''}`}
                      >
                        <div className="icon-wrapper"><i className="fa-solid fa-chart-line"></i></div>
                        <div className="text-wrapper"><span>Growth Monitor</span></div>
                      </ProtectedLink>
                    </li>
                  )}

                  {hasCoverageAnalysisPrivilege && (
                    <li className="tm-text-primary">
                      <ProtectedLink
                        to="/coverage-analysis"
                        vgroup="monitor_growth"
                        action="view"
                        applicationType="Admin Transmora"
                        className={`text-decoration-none ${location.pathname.includes("/coverage-analysis") ? 'active' : ''}`}
                      >
                        <div className="icon-wrapper"><i className="fa-solid fa-map-location-dot"></i></div>
                        <div className="text-wrapper"><span>Workload & Coverage Analysis</span></div>
                      </ProtectedLink>
                    </li>
                  )}

                  {hasStoreDirectorySupplyMonitorPrivilege && (
                    <li className="tm-text-primary">
                      <ProtectedLink
                        to="/store-directory-supply-monitor"
                        vgroup="project"
                        action="view"
                        applicationType="Admin Transmora"
                        className={`text-decoration-none d-flex align-items-center ${location.pathname.includes("/store-directory-supply-monitor") ? 'active' : ''}`}
                      >
                        <div className="icon-wrapper"><i className="fa-solid fa-map"></i></div>
                        <div className="text-wrapper"><span>Store Directory Supply Monitor</span></div>
                      </ProtectedLink>
                    </li>
                  )}

                  {hasProjectPrivilege && (
                    <li className="tm-text-primary">
                      <ProtectedLink
                        to="/project"
                        vgroup="project"
                        action="view"
                        applicationType="Admin Transmora"
                        className={`text-decoration-none ${location.pathname.includes("/project") ? 'active' : ''}`}
                      >
                        <div className="icon-wrapper"><i className="fa-solid fa-diagram-project"></i></div>
                        <div className="text-wrapper"><span>Projects</span></div>
                      </ProtectedLink>
                    </li>
                  )}

                  {/* {hasProjectPrivilege && ( */}
                  <li className="tm-text-primary">
                    <Link
                      to="/calendar"
                      // vgroup="project"
                      // action="view"
                      // applicationType="Admin Transmora"
                      className={`text-decoration-none ${location.pathname.includes("/calendar") ? 'active' : ''}`}
                    >
                      <div className="icon-wrapper"><i className="fa-solid fa-calendar-days"></i></div>
                      <div className="text-wrapper"><span>Calendar</span></div>
                    </Link>
                  </li>
                  {/* )} */}

                  {hasProjectPrivilege && (
                    <li className="tm-text-primary">
                      <ProtectedLink
                        to="/assist"
                        vgroup="project"
                        action="view"
                        applicationType="Admin Transmora"
                        className={`text-decoration-none ${location.pathname.includes("/assist") ? 'active' : ''}`}
                      >
                        <div className="icon-wrapper"><i className="fa-solid fa-user-pen"></i></div>
                        <div className="text-wrapper"><span>Assist</span></div>
                      </ProtectedLink>
                    </li>
                  )}
                </ul>
              </li>

              {hasDataSection && (
                <li>
                  <p>Datas</p>
                  <ul className="p-0">
                    {hasClientPrivilege && (
                      <li className="tm-text-primary">
                        <ProtectedLink
                          to="/client"
                          vgroup="client"
                          action="view"
                          applicationType="Admin Transmora"
                          className={`text-decoration-none ${location.pathname.includes("/client/") || location.pathname === "/client" ? 'active' : ''}`}
                        >
                          <div className="icon-wrapper"><i className="fa-solid fa-handshake"></i></div>
                          <div className="text-wrapper"><span>Client</span></div>
                        </ProtectedLink>
                      </li>
                    )}

                    {hasPrincipalPrivilege && (
                      <li className="tm-text-primary">
                        <ProtectedLink
                          to="/principal"
                          vgroup="principal"
                          action="view"
                          applicationType="Admin Transmora"
                          className={`text-decoration-none ${location.pathname.includes("/principal/") || location.pathname === "/principal" ? 'active' : ''}`}
                        >
                          <div className="icon-wrapper"><i className="fa-solid fa-building"></i></div>
                          <div className="text-wrapper"><span>Principal</span></div>
                        </ProtectedLink>
                      </li>
                    )}

                    {hasStorePrivilege && (
                      <li className="tm-text-primary">
                        <ProtectedLink
                          to="/store"
                          vgroup="store"
                          action="view"
                          applicationType="Admin Transmora"
                          className={`text-decoration-none ${location.pathname === "/store" || location.pathname.startsWith("/store/") ? 'active' : ''}`}
                        >
                          <div className="icon-wrapper"><i className="fa-solid fa-store"></i></div>
                          <div className="text-wrapper"><span>Store</span></div>
                        </ProtectedLink>
                      </li>
                    )}

                    {hasAreaPrivilege && (
                      <li className="tm-text-primary">
                        <ProtectedLink
                          to="/area"
                          vgroup="area"
                          action="view"
                          applicationType="Admin Transmora"
                          className={`text-decoration-none ${location.pathname.includes("/area") ? 'active' : ''}`}
                        >
                          <div className="icon-wrapper"><i className="fa-solid fa-mountain-city"></i></div>
                          <div className="text-wrapper"><span>Area</span></div>
                        </ProtectedLink>
                      </li>
                    )}

                    {hasPoiPrivilege && (
                      <li className="tm-text-primary">
                        <ProtectedLink
                          to="/poi"
                          vgroup="poi"
                          action="view"
                          applicationType="Admin Transmora"
                          className={`text-decoration-none ${location.pathname.includes("/poi") ? 'active' : ''}`}
                        >
                          <div className="icon-wrapper"><i className="fa-solid fa-location-dot"></i></div>
                          <div className="text-wrapper"><span>POI</span></div>
                        </ProtectedLink>
                      </li>
                    )}

                    {hasGoodsPrivilege && (
                      <li className="tm-text-primary">
                        <ProtectedLink
                          to="/goods"
                          vgroup="goods"
                          action="view"
                          applicationType="Admin Transmora"
                          className={`text-decoration-none ${location.pathname.includes("/goods") ? 'active' : ''}`}
                        >
                          <div className="icon-wrapper"><i className="fa-solid fa-box"></i></div>
                          <div className="text-wrapper"><span>Goods</span></div>
                        </ProtectedLink>
                      </li>
                    )}

                    {hasInstallerPrivilege && (
                      <li className="tm-text-primary">
                        <ProtectedLink
                          to="/installer"
                          vgroup="installer"
                          action="view"
                          applicationType="Admin Transmora"
                          className={`text-decoration-none ${location.pathname.includes("/installer") ? 'active' : ''}`}
                        >
                          <div className="icon-wrapper"><i className="fa-solid fa-users"></i></div>
                          <div className="text-wrapper"><span>Field User</span></div>
                        </ProtectedLink>
                      </li>
                    )}

                    {hasUserPrivilege && (
                      <li className="tm-text-primary">
                        <ProtectedLink
                          to="/user"
                          vgroup="user"
                          action="view"
                          applicationType="Admin Transmora"
                          className={`text-decoration-none ${location.pathname.includes("/user") ? 'active' : ''}`}
                        >
                          <div className="icon-wrapper"><i className="fa-solid fa-user"></i></div>
                          <div className="text-wrapper"><span>Back Office User</span></div>
                        </ProtectedLink>
                      </li>
                    )}

                    {hasRetailPartnerPrivilege && (
                      <li className="tm-text-primary">
                        <ProtectedLink
                          to="/retail-partner"
                          vgroup="merchant_partner"
                          action="view"
                          applicationType="Admin Transmora"
                          className={`text-decoration-none ${location.pathname.includes("/retail-partner") ? 'active' : ''}`}
                        >
                          <div className="icon-wrapper"><i className="fa-solid fa-handshake"></i></div>
                          <div className="text-wrapper"><span>Merchant Partner</span></div>
                        </ProtectedLink>
                      </li>
                    )}


                    {hasFormPrivilege && (
                      <li className="tm-text-primary">
                        <ProtectedLink
                          to="/form"
                          vgroup="form"
                          action="view"
                          applicationType="Admin Transmora"
                          className={`text-decoration-none ${location.pathname.includes("/form") ? 'active' : ''}`}
                        >
                          <div className="icon-wrapper"><i className="fa-solid fa-clipboard-list"></i></div>
                          <div className="text-wrapper"><span>Forms</span></div>
                        </ProtectedLink>
                      </li>
                    )}

                    {hasTransactionPrivilege && (
                      <li className="tm-text-primary">
                        <ProtectedLink
                          to="/transaction"
                          vgroup="form"
                          action="view"
                          applicationType="Admin Transmora"
                          className={`text-decoration-none ${location.pathname.includes("/transaction") ? 'active' : ''}`}
                        >
                          <div className="icon-wrapper"><i className="fa-solid fa-file-invoice-dollar"></i></div>
                          <div className="text-wrapper"><span>Transactions</span></div>
                        </ProtectedLink>
                      </li>
                    )}

                    {hasSystemParameterPrivilege && (
                      <li className="tm-text-primary">
                        <ProtectedLink
                          to="/system-parameter"
                          vgroup="system_parameter"
                          action="view"
                          applicationType="Admin Transmora"
                          className={`text-decoration-none ${location.pathname.includes("/system-parameter") ? 'active' : ''}`}
                        >
                          <div className="icon-wrapper"><i className="fa-solid fa-cogs"></i></div>
                          <div className="text-wrapper"><span>System Parameter</span></div>
                        </ProtectedLink>
                      </li>
                    )}

                    {hasTribePrivilege && (
                      <li className="tm-text-primary">
                        <ProtectedLink
                          to="/tribe"
                          vgroup="tribe"
                          action="view"
                          applicationType="Admin Transmora"
                          className={`text-decoration-none ${location.pathname.includes("/tribe") ? 'active' : ''}`}
                        >
                          <div className="icon-wrapper"><i className="fa-solid fa-users"></i></div>
                          <div className="text-wrapper"><span>Tribe</span></div>
                        </ProtectedLink>
                      </li>
                    )}

                    {hasDistributionHubPrivilege && (
                      <li className="tm-text-primary">
                        <ProtectedLink
                          to="/distribution-hub"
                          vgroup="distribution_hub"
                          action="view"
                          applicationType="Admin Transmora"
                          className={`text-decoration-none ${location.pathname.includes("/distribution-hub") ? 'active' : ''}`}
                        >
                          <div className="icon-wrapper"><i className="fa fa-cubes"></i></div>
                          <div className="text-wrapper"><span>Distribution Hub</span></div>
                        </ProtectedLink>
                      </li>
                    )}
                  </ul>
                </li>
              )}

              {hasClientTransmoraSection && (
                <li>
                  <p>Client Transmore</p>
                  <ul className="p-0">
                    {hasProjectConfigPrivilege && (
                      <li className="tm-text-primary">
                        <ProtectedLink
                          to="/client-project-config"
                          vgroup="project"
                          action="view"
                          applicationType="Admin Transmora"
                          className={`text-decoration-none d-flex align-items-center ${location.pathname.includes("/client-project-config") ? 'active' : ''}`}
                        >
                          <div className="icon-wrapper"><i className="fa-solid fa-cog"></i></div>
                          <div className="text-wrapper"><span>Project Config</span></div>
                        </ProtectedLink>
                      </li>
                    )}

                    {hasProjectKeyPrivilege && (
                      <li className="tm-text-primary">
                        <ProtectedLink
                          to="/client-project-key"
                          vgroup="project"
                          action="view"
                          applicationType="Admin Transmora"
                          className={`text-decoration-none d-flex align-items-center ${location.pathname.includes("/client-project-key") ? 'active' : ''}`}
                        >
                          <div className="icon-wrapper"><i className="fa-solid fa-key"></i></div>
                          <div className="text-wrapper"><span>Project Key</span></div>
                        </ProtectedLink>
                      </li>
                    )}

                    {hasProjectLayoutPrivilege && (
                      <li className="tm-text-primary">
                        <ProtectedLink
                          to="/client-project-layout"
                          vgroup="project"
                          action="view"
                          applicationType="Admin Transmora"
                          className={`text-decoration-none d-flex align-items-center ${location.pathname.includes("/client-project-layout") ? 'active' : ''}`}
                        >
                          <div className="icon-wrapper"><i className="fa-solid fa-th-large"></i></div>
                          <div className="text-wrapper"><span>Project Layout</span></div>
                        </ProtectedLink>
                      </li>
                    )}

                    {hasProjectFilterPrivilege && (
                      <li className="tm-text-primary">
                        <ProtectedLink
                          to="/client-project-filter"
                          vgroup="project"
                          action="view"
                          applicationType="Admin Transmora"
                          className={`text-decoration-none d-flex align-items-center ${location.pathname.includes("/client-project-filter") ? 'active' : ''}`}
                        >
                          <div className="icon-wrapper"><i className="fa-solid fa-filter"></i></div>
                          <div className="text-wrapper"><span>Project Filter</span></div>
                        </ProtectedLink>
                      </li>
                    )}

                    {hasProjectMetricPrivilege && (
                      <li className="tm-text-primary">
                        <ProtectedLink
                          to="/client-project-metric"
                          vgroup="project"
                          action="view"
                          applicationType="Admin Transmora"
                          className={`text-decoration-none d-flex align-items-center ${location.pathname.includes("/client-project-metric") ? 'active' : ''}`}
                        >
                          <div className="icon-wrapper"><i className="fa-solid fa-area-chart"></i></div>
                          <div className="text-wrapper"><span>Project Metric</span></div>
                        </ProtectedLink>
                      </li>
                    )}

                    {hasProjectUserPrivilege && (
                      <li className="tm-text-primary">
                        <ProtectedLink
                          to="/client-project-user"
                          vgroup="client_project_user"
                          action="view"
                          applicationType="Admin Transmora"
                          className={`text-decoration-none d-flex align-items-center ${location.pathname.includes("/client-project-user") ? 'active' : ''}`}
                        >
                          <div className="icon-wrapper"><i className="fa-solid fa-user-tie"></i></div>
                          <div className="text-wrapper"><span>Project User</span></div>
                        </ProtectedLink>
                      </li>
                    )}

                  </ul>
                </li>
              )}

              {hasNexusDistributorSection && (
                <li>
                  <p>More Nexus</p>
                  <ul className="p-0">
                    {hasNexusDistributorPrivilege && (
                      <li className="tm-text-primary">
                        <ProtectedLink
                          to="/nexus-distributor"
                          vgroup="nexus_distributor"
                          action="view"
                          applicationType="Admin Transmora"
                          className={`text-decoration-none d-flex align-items-center ${location.pathname.includes("/nexus-distributor") ? 'active' : ''}`}
                        >
                          <div className="icon-wrapper"><i className="fa-solid fa-truck"></i></div>
                          <div className="text-wrapper"><span>Nexus Distributor</span></div>
                        </ProtectedLink>
                      </li>
                    )}
                  </ul>
                </li>
              )}


              {hasBankIntegrationSection && (
                <li>
                  <p>Amar Bank Integration</p>
                  <ul className="p-0">
                    {hasBankAccountPrivilege && (
                      <li className="tm-text-primary">
                        <ProtectedLink
                          to="/bankAccount"
                          vgroup="amar_bank_account_registration"
                          action="view"
                          applicationType="Admin Transmora"
                          className={`text-decoration-none d-flex align-items-center ${location.pathname.includes("/bankAccount") ? 'active' : ''}`}
                        >
                          <div className="icon-wrapper"><i className="fa-solid fa-building-columns"></i></div>
                          <div className="text-wrapper"><span>Bank Account Registration</span></div>
                        </ProtectedLink>
                      </li>
                    )}

                    {hasQrisTransactionPrivilege && (
                      <li className="tm-text-primary">
                        <ProtectedLink
                          to="/qristransaction"
                          vgroup="amar_qris_transaction_status"
                          action="view"
                          applicationType="Admin Transmora"
                          className={`text-decoration-none d-flex align-items-center ${location.pathname.includes("/qristransaction") ? 'active' : ''}`}
                        >
                          <div className="icon-wrapper"><i className="fa-solid fa-qrcode"></i></div>
                          <div className="text-wrapper"><span>Qris Transaction Status</span></div>
                        </ProtectedLink>
                      </li>
                    )}

                    {hasTimeDepositPrivilege && (
                      <li className="tm-text-primary">
                        <ProtectedLink
                          to="/timeDeposit"
                          vgroup="amar_time_deposit_status"
                          action="view"
                          applicationType="Admin Transmora"
                          className={`text-decoration-none d-flex align-items-center ${location.pathname.includes("/timeDeposit") ? 'active' : ''}`}
                        >
                          <div className="icon-wrapper"><i className="fa-solid fa-money-bill-transfer"></i></div>
                          <div className="text-wrapper"><span>Time Deposit Status</span></div>
                        </ProtectedLink>
                      </li>
                    )}

                    {hasTransferStatusPrivilege && (
                      <li className="tm-text-primary">
                        <ProtectedLink
                          to="/transferStatus"
                          vgroup="amar_transfer_status"
                          action="view"
                          applicationType="Admin Transmora"
                          className={`text-decoration-none ${location.pathname.includes("/transferStatus") ? 'active' : ''}`}
                        >
                          <div className="icon-wrapper"><i className="fa-solid fa-money-check"></i></div>
                          <div className="text-wrapper"><span>Transfer Status</span></div>
                        </ProtectedLink>
                      </li>
                    )}
                  </ul>
                </li>
              )}

              {hasConfigurationSection && (
                <li>
                  <p>Configurations</p>
                  <ul className="p-0">
                    {hasRBACPrivilege && (
                      <li className="tm-text-primary">
                        <ProtectedLink
                          to="/rbac"
                          vgroup="role_access"
                          action="view"
                          applicationType="Admin Transmora"
                          className={`text-decoration-none ${location.pathname.includes("/rbac") ? 'active' : ''}`}
                        >
                          <div className="icon-wrapper"><i className="fa-solid fa-person"></i></div>
                          <div className="text-wrapper"><span>RBAC</span></div>
                        </ProtectedLink>
                      </li>
                    )}


                    {hasVersionPrivilege && (
                      <li className="tm-text-primary">
                        <ProtectedLink
                          to="/version"
                          vgroup="version"
                          action="view"
                          applicationType="Admin Transmora"
                          className={`text-decoration-none ${location.pathname.includes("/version") ? 'active' : ''}`}
                        >
                          <div className="icon-wrapper"><i className="fa-solid fa-mobile-screen"></i></div>
                          <div className="text-wrapper"><span>Versions</span></div>
                        </ProtectedLink>
                      </li>
                    )}

                    {hasHomeShortcutPrivilege && (
                      <li className="tm-text-primary">
                        <ProtectedLink
                          to="/mobile-shortcut"
                          vgroup="version"
                          action="view"
                          applicationType="Admin Transmora"
                          className={`text-decoration-none ${location.pathname.includes("/mobile-shortcut") ? 'active' : ''}`}
                        >
                          <div className="icon-wrapper"><i className="fa-solid fa-grip-horizontal"></i></div>
                          <div className="text-wrapper"><span>Mobile Shortcut</span></div>
                        </ProtectedLink>
                      </li>
                    )}

                    {hasPpobPrivilege && (
                      <>
                        <li className="tm-text-primary">
                          <a
                            href="#ppob"
                            onClick={(e) => {
                              e.preventDefault();
                              setIsPpobSubmenuOpen((prev) => !prev);
                            }}
                            className="text-decoration-none"
                            style={{ background: 'transparent', backgroundColor: 'transparent' }}
                          >
                            <div className="icon-wrapper">
                              <i className={`fa-solid fa-credit-card ${location.pathname.includes("/ppob") ? 'text-primary' : ''}`}></i>
                            </div>
                            <div className="text-wrapper flex-grow-1 d-flex align-items-center justify-content-between pe-2">
                              <span className={location.pathname.includes("/ppob") ? 'text-primary fw-semibold' : ''}>PPOB</span>
                              <i className={`fa-solid fa-chevron-${isPpobSubmenuOpen ? 'up' : 'down'} ${location.pathname.includes("/ppob") ? 'text-primary' : 'text-secondary'} small ms-2`} style={{ fontSize: '0.75rem' }}></i>
                            </div>
                          </a>
                        </li>

                        {isPpobSubmenuOpen && (
                          <>
                            <li className="tm-text-primary ms-3">
                              <ProtectedLink
                                to="/ppob"
                                vgroup="version"
                                action="view"
                                applicationType="Admin Transmora"
                                className={`text-decoration-none ${location.pathname === "/ppob" || (location.pathname.startsWith("/ppob/") && !location.pathname.includes("/ppob/history") && !location.pathname.startsWith("/ppob/items")) ? 'active' : ''
                                  }`}
                              >
                                <div className="icon-wrapper">
                                  <i className="fa-solid fa-gear" style={{ fontSize: '0.9rem' }}></i>
                                </div>
                                <div className="text-wrapper">
                                  <span>PPOB Configuration</span>
                                </div>
                              </ProtectedLink>
                            </li>
                            <li className="tm-text-primary ms-3">
                              <ProtectedLink
                                to="/ppob/items"
                                vgroup="version"
                                action="view"
                                applicationType="Admin Transmora"
                                className={`text-decoration-none ${location.pathname.startsWith("/ppob/items") ? 'active' : ''
                                  }`}
                              >
                                <div className="icon-wrapper">
                                  <i className="fa-solid fa-boxes-stacked" style={{ fontSize: '0.9rem' }}></i>
                                </div>
                                <div className="text-wrapper">
                                  <span>PPOB Items</span>
                                </div>
                              </ProtectedLink>
                            </li>
                            <li className="tm-text-primary ms-3">
                              <ProtectedLink
                                to="/ppob/history"
                                vgroup="version"
                                action="view"
                                applicationType="Admin Transmora"
                                className={`text-decoration-none ${location.pathname === "/ppob/history" ? 'active' : ''
                                  }`}
                              >
                                <div className="icon-wrapper">
                                  <i className="fa-solid fa-clock-rotate-left" style={{ fontSize: '0.9rem' }}></i>
                                </div>
                                <div className="text-wrapper">
                                  <span>PPOB Transaction History</span>
                                </div>
                              </ProtectedLink>
                            </li>
                          </>
                        )}
                      </>
                    )}
                  </ul>
                </li>
              )}

              {hasReportSection && (
                <li>
                  <p>Reports</p>
                  <ul className="p-0">
                    {hasDisbursementPrivilege && (
                      <li className="tm-text-primary">
                        <ProtectedLink
                          to="/disbursement"
                          vgroup="disbursement"
                          action="view"
                          applicationType="Admin Transmora"
                          className={`text-decoration-none ${location.pathname.includes("/disbursement") ? 'active' : ''}`}
                        >
                          <div className="icon-wrapper"><i className="fa-solid fa-chart-diagram"></i></div>
                          <div className="text-wrapper"><span>Disbursement</span></div>
                        </ProtectedLink>
                      </li>
                    )}

                    {hasReportEnginePrivilege && (
                      <li className="tm-text-primary">
                        <ProtectedLink
                          to="/report-engine"
                          vgroup="report_engine"
                          action="view"
                          applicationType="Admin Transmora"
                          className={`text-decoration-none ${location.pathname.includes("/report-engine") ? 'active' : ''}`}
                        >
                          <div className="icon-wrapper"><i className="fa-solid fa-code-commit"></i></div>
                          <div className="text-wrapper"><span>Report Engine</span></div>
                        </ProtectedLink>
                      </li>
                    )}
                  </ul>
                </li>
              )}

              {!hasDashboardPrivilege && !hasMonitorPrivilege && !hasProjectPrivilege &&
                !hasDataSection && !hasBankIntegrationSection && !hasConfigurationSection && !hasReportSection && (
                  <li>
                    <div className="p-4 text-sm text-center text-gray-500">
                      No accessible menus. Contact administrator.
                    </div>
                  </li>
                )}
            </ul>
          </nav>
        </div>

        {isSidebarOpen && (
          <div
            className="sidebar-overlay"
            onClick={() => setIsSidebarOpen(false)}
            aria-hidden="true"
          />
        )}

        <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', flex: 1, minWidth: 0 }}>
          <nav className="px-3 navbar navbar-expand-lg navbar-dark">
            <button
              className="bg-transparent border-0 text-white"
              onClick={() => setIsSidebarOpen(prev => !prev)}
              aria-label={isSidebarOpen ? 'Close sidebar' : 'Open sidebar'}
              aria-expanded={isSidebarOpen}
              title={isSidebarOpen ? 'Close sidebar' : 'Open sidebar'}
            >
              {isSidebarOpen ? <i className="fa-solid fa-xmark" /> : <i className="fa-solid fa-bars" />}
            </button>

            <div className="ms-auto d-flex align-items-center">
              <NotificationDropdown />
              <div className="dropdown text-end position-relative" ref={profileDropdownRef}>
                <button
                  className="dropdown-toggle d-flex align-items-center"
                  type="button"
                  id="dropdownMenuButton"
                  onClick={() => setIsProfileMenuOpen(prev => !prev)}
                  aria-expanded={isProfileMenuOpen}
                  style={{ color: 'white', border: 0, background: 'none', height: 40, cursor: 'pointer' }}
                >
                  <img
                    src={profile?.data?.profilePicture || "/assets/icon/user-icon.png"}
                    alt="Profile"
                    className="rounded-circle me-2"
                    width="30"
                    height="30"
                    style={{ objectFit: "cover" }}
                  />
                  Hi, {profile?.data?.name}
                </button>
                {isProfileMenuOpen && (
                  <ul
                    className="dropdown-menu dropdown-menu-end show animate-pop-in"
                    aria-labelledby="dropdownMenuButton"
                    style={{
                      position: 'absolute',
                      right: 0,
                      top: '100%',
                      marginTop: '8px',
                      zIndex: 1050,
                      minWidth: '175px',
                      fontSize: '0.84rem',
                      boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
                      borderRadius: '8px',
                      padding: '4px 0',
                      border: '1px solid rgba(0,0,0,0.08)',
                      transformOrigin: 'top right'
                    }}
                  >
                    <li>
                      <button
                        className="dropdown-item d-flex align-items-center gap-2"
                        type="button"
                        style={{ fontSize: '0.84rem', padding: '6px 14px' }}
                        onClick={() => {
                          setIsProfileMenuOpen(false);
                          navigate('/manage-profile');
                        }}
                      >
                        <i className="fa-solid fa-user-gear text-secondary" style={{ width: 16, fontSize: '0.82rem' }} />
                        <span>Manage Profile</span>
                      </button>
                    </li>
                    <li>
                      <button
                        className="dropdown-item d-flex align-items-center gap-2"
                        type="button"
                        style={{ fontSize: '0.84rem', padding: '6px 14px' }}
                        onClick={() => {
                          setIsProfileMenuOpen(false);
                          navigate('/change-password');
                        }}
                      >
                        <i className="fa-solid fa-key text-secondary" style={{ width: 16, fontSize: '0.82rem' }} />
                        <span>Change Password</span>
                      </button>
                    </li>
                    <li><hr className="dropdown-divider my-1" /></li>
                    <li>
                      <button
                        className="dropdown-item d-flex align-items-center gap-2 text-danger"
                        type="button"
                        style={{ fontSize: '0.84rem', padding: '6px 14px' }}
                        onClick={() => {
                          setIsProfileMenuOpen(false);
                          handleLogout();
                        }}
                      >
                        <i className="fa-solid fa-arrow-right-from-bracket text-danger" style={{ width: 16, fontSize: '0.82rem' }} />
                        <span>Logout</span>
                      </button>
                    </li>
                  </ul>
                )}
              </div>
            </div>
          </nav>

          <div style={{ flex: 1, overflowY: 'auto', minWidth: 0 }}>
            <Outlet />
          </div>
        </div>
      </div>
    </NotificationProvider>
  );
};

export default Home;